// src/pages/SafetyTips.jsx

import React from "react";
import styled, { createGlobalStyle, keyframes } from "styled-components";
import { connect } from "react-redux";
import {
  Shield,
  AlertTriangle,
  Eye,
  MessageSquare,
  CreditCard,
  Users,
  CheckCircle,
  UserCheck,
  Lock,
  Phone,
  MapPin,
  Clock,
} from "lucide-react";

import NavBar from "../components/layout/NavBar";
import Meta from "../components/Meta";

//================================================================//
//  🎨 DESIGN SYSTEM: TOKENS & THEME (Matching other pages)
//================================================================//

const theme = {
  colors: {
    primary: "#3b82f6",
    primaryLight: "#eff6ff",
    primaryText: "#3b82f6",
    textHeading: "#0f172a",
    textBody: "#334155",
    textMuted: "#64748b",
    background: "#f8fafc",
    surface: "#ffffff",
    border: "#e2e8f0",
    success: "hsl(145, 63%, 42%)",
    warning: "#f59e0b",
    danger: "#dc2626",
  },
  shadows: {
    sm: "0 1px 2px rgba(15, 23, 42, 0.06)",
    md: "0 6px 20px rgba(15, 23, 42, 0.07)",
  },
  font: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  radius: "14px",
};

//================================================================//
//  ✨ ANIMATIONS & GLOBAL STYLES
//================================================================//

const fadeIn = keyframes`from { opacity: 0; } to { opacity: 1; }`;
const slideInUp = keyframes`from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); }`;

const GlobalStyle = createGlobalStyle`
  body {
    background-color: ${theme.colors.background};
    font-family: ${theme.font};
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }
`;

//================================================================//
//  📄 PAGE LAYOUT & STRUCTURE
//================================================================//

const PageWrapper = styled.div`
  background: ${theme.colors.background};
  padding-top: 64px;
  min-height: 100vh;
  animation: ${fadeIn} 0.5s ease;
`;

const ContentContainer = styled.div`
  max-width: 1024px;
  margin: 0 auto;
  padding: 2rem;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const Hero = styled.section`
  text-align: center;
  padding: 3rem 1rem 2rem;
`;

const Title = styled.h1`
  font-size: 3rem;
  font-weight: 700;
  color: ${theme.colors.textHeading};
  margin: 0;
  letter-spacing: -0.03em;
  line-height: 1.1;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const Subtitle = styled.p`
  max-width: 700px;
  margin: 1rem auto 0;
  font-size: 1.125rem;
  color: ${theme.colors.textMuted};
  line-height: 1.6;
`;

const ContentPane = styled.div`
  background: ${theme.colors.surface};
  border-radius: ${theme.radius};
  box-shadow: ${theme.shadows.md};
  padding: 2.5rem;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const SectionHeader = styled.h2`
  font-size: 1.75rem;
  font-weight: 600;
  color: ${theme.colors.textHeading};
  margin: 0 0 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;

  svg {
    color: ${theme.colors.primary};
  }
`;

const TipsGrid = styled.div`
  display: grid;
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const TipCard = styled.div`
  display: flex;
  gap: 1rem;
  padding: 1.5rem;
  background: ${theme.colors.background};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radius};
  animation: ${slideInUp} 0.6s ease-out;
`;

const TipIcon = styled.div`
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: ${props => {
    if (props.$type === 'warning') return '#fef3cd';
    if (props.$type === 'danger') return '#fee2e2';
    if (props.$type === 'success') return '#dcfce7';
    return theme.colors.primaryLight;
  }};
  color: ${props => {
    if (props.$type === 'warning') return theme.colors.warning;
    if (props.$type === 'danger') return theme.colors.danger;
    if (props.$type === 'success') return theme.colors.success;
    return theme.colors.primary;
  }};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const TipContent = styled.div`
  flex: 1;
`;

const TipTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: ${theme.colors.textHeading};
  margin: 0 0 0.5rem;
`;

const TipText = styled.p`
  font-size: 0.95rem;
  color: ${theme.colors.textMuted};
  margin: 0;
  line-height: 1.6;
`;

const HighlightBox = styled.div`
  background: linear-gradient(135deg, ${theme.colors.primaryLight}, rgba(255, 255, 255, 0.8));
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radius};
  padding: 2rem;
  margin: 2rem 0;
  text-align: center;
`;

const HighlightTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  color: ${theme.colors.textHeading};
  margin: 0 0 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;

  svg {
    color: ${theme.colors.primary};
  }
`;

const HighlightText = styled.p`
  font-size: 1.1rem;
  color: ${theme.colors.textBody};
  margin: 0;
  line-height: 1.6;
`;

//================================================================//
//  MAIN COMPONENT
//================================================================//

const SafetyTips = ({ state }) => {
  const safetyTips = [
    {
      icon: <MessageSquare size={24} />,
      title: "Communicate Through Platform",
      text: "Keep all communication within Doitly's chat system. This creates a record and helps protect both parties.",
      type: "primary"
    },
    {
      icon: <CreditCard size={24} />,
      title: "Agree on Payment Terms",
      text: "Clearly discuss payment method, amount, and timing before starting work. Popular options include Venmo, Zelle, PayPal, or cash.",
      type: "primary"
    },
    {
      icon: <Eye size={24} />,
      title: "Meet in Public Places",
      text: "For in-person tasks, meet in well-lit, public areas first. Trust your instincts and prioritize your safety.",
      type: "success"
    },
    {
      icon: <Clock size={24} />,
      title: "Pay After Completion",
      text: "Payment should be made after work is completed to your satisfaction, as agreed upon beforehand.",
      type: "primary"
    },
    {
      icon: <AlertTriangle size={24} />,
      title: "Avoid Upfront Payments",
      text: "Be cautious of requests for upfront payments for materials or supplies, especially from new users.",
      type: "warning"
    },
    {
      icon: <Users size={24} />,
      title: "Check User Profiles",
      text: "Review user profiles and previous work examples before accepting proposals or hiring.",
      type: "primary"
    }
  ];

  return (
    <>
      <GlobalStyle />
      <Meta
        pageMeta={{
          title: "Safety Tips - Doitly",
          description: "Stay safe while using Doitly. Learn essential safety tips for task posters and service providers to protect yourself and have positive experiences."
        }}
      />
      <PageWrapper>
        <NavBar user={state.user} />
        <ContentContainer>
          <Hero>
            <Title>
              Stay <span style={{ color: theme.colors.primary }}>Safe</span> on Doitly
            </Title>
            <Subtitle>
              Your safety is our priority. Follow these guidelines to protect yourself 
              and ensure positive experiences on our platform.
            </Subtitle>
          </Hero>

          <ContentPane>
            <SectionHeader>
              <Shield size={28} />
              Essential Safety Tips
            </SectionHeader>
            <TipsGrid>
              {safetyTips.map((tip, index) => (
                <TipCard key={index}>
                  <TipIcon $type={tip.type}>{tip.icon}</TipIcon>
                  <TipContent>
                    <TipTitle>{tip.title}</TipTitle>
                    <TipText>{tip.text}</TipText>
                  </TipContent>
                </TipCard>
              ))}
            </TipsGrid>
          </ContentPane>

          <HighlightBox>
            <HighlightTitle>
              <CheckCircle size={24} />
              Doitly Email Verification
            </HighlightTitle>
            <HighlightText>
              All users must verify their email addresses during signup to create an account. 
              This helps ensure legitimate users and adds an extra layer of security to our platform.
            </HighlightText>
          </HighlightBox>

          <HighlightBox>
            <HighlightTitle>
              <Shield size={24} />
              Trust Your Instincts
            </HighlightTitle>
            <HighlightText>
              If something feels wrong or too good to be true, trust your gut. 
              Take time to verify information and don't rush into agreements. 
              Your safety and security are worth more than any task or payment.
            </HighlightText>
          </HighlightBox>
        </ContentContainer>
      </PageWrapper>
    </>
  );
};

const mapStateToProps = (state) => ({ state });
export default connect(mapStateToProps)(SafetyTips);